import * as angular from 'angular';
import {
    getSmartHomeStarterKit,
    getSmartHomeStarterKitActivationDate,
    getSmartHomeStarterKitDiscountRemovalDate,
    getSmartHomeStarterKitIsActivated,
    hasSmartHomeStarterKit,
    isAdvancedSmartHomeKit,
    isAdvancedSmartHomeKitState,
    isDp3Policy,
    isPastActivationDatePeriod
} from 'common/source/utilities/policyInfoHelper';
import { DiscountsHelper } from 'utilities/discountsHelper';
import { AMERICAN_DATE_FORMAT_ORDINAL_MEDIUM, getMomentFromHippoDateString } from 'common/source/utilities/dates';

export interface DiscountForDisplay {
    label: string;
    discountWasReceived: boolean;
    discountWasOffered: boolean;
    tooltipText: string;
    testId: string;
}

// disabling rule in this case because this function is just a series of pushing entries into an array
// tslint:disable-next-line:no-big-function
export function getDiscounts(
    policyInfo: CustomerPolicyInfo,
    smartHomeDiscount: number,
    proMonitoringStates: Array<string>
): Array<DiscountForDisplay> {
    /*
    Per the spec:
    Don’t show the "Years at Residence" discount at all, regardless if selected or not by the user / agent
     */
    const helper = new DiscountsHelper(policyInfo);

    const discounts: Array<DiscountForDisplay> = [];

    let multiplePoliciesDiscountLabel = 'Auto Insurance Policy';
    let multiplePoliciesDiscountTooltipText =
        'You or your spouse have an active auto insurance policy with ANY insurance company';

    if (isDp3Policy(policyInfo)) {
        multiplePoliciesDiscountLabel = 'Repeating customer';
        multiplePoliciesDiscountTooltipText =
            'As a Hippo customer, you get an additional discount when insuring your investment property with us';
    }

    discounts.push({
        label: multiplePoliciesDiscountLabel,
        discountWasReceived: helper.receivedMultiplePoliciesDiscount(),
        discountWasOffered: helper.wasOfferedMultiplePoliciesDiscount(),
        tooltipText: multiplePoliciesDiscountTooltipText,
        testId: 'multiple_policies_discount'
    });

    if (helper.receivedEarlyBirdDiscount()) {
        /*

        From the spec:

        "Don’t show it if wasn't applied, since customers
        won’t be able to select it after purchase"

        */

        discounts.push({
            label: 'Early Bird',
            discountWasReceived: helper.receivedEarlyBirdDiscount(),
            discountWasOffered: helper.wasOfferedEarlyBirdDiscount(),
            tooltipText: 'You purchased your policy 1 or more days in advance',
            testId: 'early_bird_discount'
        });
    }

    if (helper.receivedRecentHomePurchaseDiscount()) {
        /*

        From the spec:

        "If the discount isn’t applicable to the customer,
        DON’T show it at all in this page - since the customer
        can’t take any action to gain this discount"

        */

        discounts.push({
            label: 'New Home Purchase',
            discountWasReceived: helper.receivedRecentHomePurchaseDiscount(),
            discountWasOffered: helper.wasOfferedRecentHomePurchaseDiscount(),
            tooltipText: 'You bought a new home or have owned your home for less than a year',
            testId: 'new_home_purchase_discount'
        });
    }

    if (helper.receivedComcastAffinityDiscount()) {
        // spec says we should not show this discount unless it was received

        discounts.push({
            label: 'Comcast Discount',
            discountWasReceived: helper.receivedComcastAffinityDiscount(),
            discountWasOffered: helper.wasOfferedComcastAffinityDiscount(),
            tooltipText: 'Additional discount for Comcast and Xfinity customers',
            testId: 'comcast_discount'
        });
    }

    discounts.push({
        label: 'Gated Community',
        discountWasReceived: helper.receivedGatedCommunityDiscount(),
        discountWasOffered: helper.wasOfferedGatedCommunityDiscount(),
        tooltipText: 'Your home is part of a fenced-in community',
        testId: 'gated_community_discount'
    });

    discounts.push({
        label: "Homeowner's Association",
        discountWasReceived: helper.receivedHomeOwnersAssociationDiscount(),
        discountWasOffered: helper.wasOfferedHomeOwnersAssociationDiscount(),
        tooltipText: "You're a member of a homeowner's association",
        testId: 'home_owners_association_discount'
    });

    discounts.push({
        label: 'No Mortgage',
        discountWasReceived: helper.receivedNoMortgageDiscount(),
        discountWasOffered: helper.wasOfferedNoMortgageDiscount(),
        tooltipText: 'There is no existing mortgage on the property and you are not currently seeking one',
        testId: 'no_mortgage_discount'
    });

    discounts.push({
        label: 'Storm Shutters',
        discountWasReceived: helper.receivedStormShuttersDiscount(),
        discountWasOffered: helper.wasOfferedStormShuttersDiscount(),
        tooltipText: 'You have storm shutters installed on your windows',
        testId: 'storm_shutters_discount'
    });

    discounts.push({
        label: 'Wind Coverage Exclusion',
        discountWasReceived: helper.receivedWindCoverageExclusionDiscount(),
        discountWasOffered: helper.wasOfferedWindCoverageExclusionDiscount(),
        tooltipText: 'Excludes wind and hail related damage from your policy',
        testId: 'wind_coverage_exclusion_discount'
    });

    discounts.push({
        label: 'Windstorm Resistant Home',
        discountWasReceived: helper.receivedWindResistantHomeDiscount(),
        discountWasOffered: helper.wasOfferedWindResistantHomeDiscount(),
        tooltipText:
            'Your home is certified by a specific program to withstand windstorm damage (certification required)',
        testId: 'wind_resistant_home_discount'
    });

    discounts.push({
        label: 'Theft Alarm',
        discountWasReceived: helper.receivedTheftAlarmDiscount(),
        discountWasOffered: helper.wasOfferedTheftAlarmDiscount(),
        tooltipText: 'A theft alarm which is monitored by a security company or a police station',
        testId: 'theft_alarm_discount'
    });

    discounts.push({
        label: 'Fire Alarm',
        discountWasReceived: helper.receivedFireAlarmDiscount(),
        discountWasOffered: helper.wasOfferedFireAlarmDiscount(),
        tooltipText: 'A fire alarm which is monitored by a security company or a fire station',
        testId: 'fire_alarm_discount'
    });

    discounts.push({
        label: 'Water Sensor / Valve Closure',
        discountWasReceived: helper.receivedWaterLeakSensorOrValveClosureDevices(),
        discountWasOffered: helper.wasOfferedWaterLeakSensorOrValveClosureDevices(),
        tooltipText: 'Water leak detection sensors or valve closure devices',
        testId: 'water_sensor_discount'
    });

    discounts.push({
        label: 'Hail Resistant Roof',
        discountWasReceived: helper.receivedHailResistantRoofDiscount(),
        discountWasOffered: helper.wasOfferedHailResistantRoofDiscount(),
        tooltipText: 'You have a hail resistant roof that has been certified by a licensed contractor',
        testId: 'hail_resistant_roof_discount'
    });

    discounts.push({
        label: 'Fire Extinguisher',
        discountWasReceived: helper.receivedFireExtinguisherDiscount(),
        discountWasOffered: helper.wasOfferedFireExtinguisherDiscount(),
        tooltipText: 'You have one or more fire extinguishers',
        testId: 'fire_extinguisher_discount'
    });

    discounts.push({
        label: 'Sprinkler System',
        discountWasReceived: helper.receivedSprinklerSystemDiscount(),
        discountWasOffered: helper.wasOfferedSprinklerSystemDiscount(),
        tooltipText: 'A built in internal sprinkler system',
        testId: 'sprinkler_system_discount'
    });

    if (smartHomeDiscount > 0) {
        getNotionDiscount(policyInfo, proMonitoringStates, discounts, helper);
    }

    discounts.push({
        label: 'Cosmetic damage exclusion',
        discountWasReceived: helper.receivedCosmeticDamageExclusionDiscount(),
        discountWasOffered: helper.wasOfferedCosmeticDamageExclusionDiscount(),
        tooltipText: 'Excludes cosmetic damage caused by windstorm or hail',
        testId: 'cosmetic_damage_exclusion'
    });

    return discounts;
}

const getNotionDiscount = (
    policyInfo: CustomerPolicyInfo,
    proMonitoringStates: Array<string>,
    discounts: Array<DiscountForDisplay>,
    helper: DiscountsHelper
) => {
    const smartHomeStarterKit = getSmartHomeStarterKit(policyInfo);
    const smartHomeStarterKitActivationDate = getSmartHomeStarterKitActivationDate(policyInfo);
    const smartHomeStarterKitWasDeactivated =
        smartHomeStarterKitActivationDate && !getSmartHomeStarterKitIsActivated(policyInfo);
    const isAdvancedSmartKit = isAdvancedSmartHomeKit(policyInfo);
    const isAdvancedSmartKitState = isAdvancedSmartHomeKitState(proMonitoringStates, policyInfo);
    const smartHomeKitLabel = isAdvancedSmartKitState ? (isAdvancedSmartKit ? 'Advanced ' : 'Basic ') : '';
    let smartHomeDeviceTooltipText = '';
    const removalDateFormatted = getSmartHomeStarterKitDiscountRemovalDate(policyInfo).format(
        AMERICAN_DATE_FORMAT_ORDINAL_MEDIUM
    );
    const smartHomeStarterKitMissedActivation =
        smartHomeStarterKit && !smartHomeStarterKitActivationDate && isPastActivationDatePeriod(policyInfo);
    if (smartHomeStarterKitMissedActivation) {
        smartHomeDeviceTooltipText = `Please activate your kit and call us at (800) 886-0318 to activate your ${smartHomeKitLabel}Smart Home
        discount.`;
    } else if (smartHomeStarterKitWasDeactivated) {
        smartHomeDeviceTooltipText = `Your smart home sensor kit has been deactivated.
        Please reactivate your sensor and call us at (800) 886-0318 to resume your Smart Home discount.`;
    } else if (getSmartHomeStarterKitIsActivated(policyInfo)) {
        if (smartHomeStarterKitActivationDate) {
            smartHomeDeviceTooltipText = `Smart home kit activated on ${smartHomeStarterKitActivationDate}.`;
        } else {
            smartHomeDeviceTooltipText = `Your ${smartHomeKitLabel}Smart Home discount will be removed on ${removalDateFormatted} unless you 
            activate your smart home sensor kit (mailed within 2 weeks after your policy effective date). For any questions, 
            call us at (800) 886-0318.`;
        }
    } else {
        smartHomeDeviceTooltipText = `You might be missing out on a discount on your premium. Activate your smart home sensor 
        kit (mailed within 2 weeks after your policy effective date). For any questions, call us at (800) 886-0318.`;
    }

    discounts.push({
        label: isAdvancedSmartKit ? 'Advanced Smart Home' : 'Smart Home Device',
        discountWasReceived: helper.receivedSmartHomeStarterKitDiscount(),
        discountWasOffered: hasSmartHomeStarterKit(policyInfo),
        tooltipText: smartHomeDeviceTooltipText,
        testId: 'smart_home_starter_kit_discount'
    });
};

// the following is to make this code accessible to unit tests

const module = angular.module('customerPortal.utilities.discountsDisplay.getDiscounts', []);

module.factory('getDiscounts', function() {
    return getDiscounts;
});
